import React, { useEffect } from 'react';
import { Button, Flex, Text, Select } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AleaArrowRightIcon from '../../icons/AleaArrowRightIcon';
import AleaArrowLeftIcon from '../../icons/AleaArrowLeftIcon';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';

interface AleaPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  perPage?: number; // Make optional since we'll get it from localStorage
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  isLoading?: boolean;
}

const AleaPagination: React.FC<AleaPaginationProps> = ({
  currentPage,
  totalPages,
  total,
  perPage: propPerPage,
  onPageChange,
  onPerPageChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();
  
  // Use prop perPage if provided, otherwise use stored value
  const effectivePerPage = propPerPage || storedPerPage;

  // Notify parent component when stored perPage changes (including initial load)
  useEffect(() => {
    if (propPerPage === undefined && onPerPageChange) {
      onPerPageChange(storedPerPage);
    }
  }, [storedPerPage, propPerPage, onPerPageChange]);

  // Notify parent component when stored perPage changes or on initial load
  useEffect(() => {
    if (!propPerPage && onPerPageChange && storedPerPage !== 10) {
      // Only notify if the stored value is different from default
      onPerPageChange(storedPerPage);
    }
  }, [storedPerPage, propPerPage, onPerPageChange]);

  // Also notify on mount if there's a stored value different from default
  useEffect(() => {
    // Mevcut kod (sorunlu):
    if (!propPerPage && onPerPageChange) {
      onPerPageChange(storedPerPage);
    }
    
    // Düzeltilmiş kod:
    if (propPerPage === undefined && onPerPageChange) {
      onPerPageChange(storedPerPage);
    }
    
    // Veya daha güvenli:
    if (typeof propPerPage === 'undefined' && onPerPageChange) {
      onPerPageChange(storedPerPage);
    }
  }, []); // Empty dependency array - runs only on mount

  // Calculate the start and end indices for the current page
  const startIndex = (currentPage - 1) * effectivePerPage + 1;
  const endIndex = Math.min(currentPage * effectivePerPage, total);

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const maxVisiblePages = 5;
    const pages: (number | string)[] = [];
  
    if (totalPages <= maxVisiblePages) {
      // If total pages are less than or equal to max visible pages, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end pages based on current page
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
      // Adjust start page if end page is at the limit
      if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - maxVisiblePages + 1);
      }
  
      // Always show first page
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
  
      // Add pages in the visible range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
  
      // Always show last page
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
  
    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && !isLoading) {
      onPageChange(page);
    }
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const newPerPage = value === 'all' ? total : parseInt(value);
    
    // Update localStorage first
    updatePerPage(newPerPage);
    
    // Then notify parent component
    if (onPerPageChange) {
      onPerPageChange(newPerPage);
    }
  };

  if (total === 0) {
    return null;
  }

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }} // Column on small screens, row on large screens
      justifyContent={{ base: 'center', lg: 'space-between' }}
      alignItems={{ base: 'center', lg: 'center' }}
      py={4}
      bg="white"
      borderTop="1px"
      borderColor="gray.200"
      minHeight={{ base: 'auto', lg: 100 }} // Responsive height
      px={6}
      gap={{ base: 4, lg: 0 }} // Add gap on small screens
    >
      {/* Left side - Results info */}
      <Text 
        fontSize="sm" 
        color="alea.500" 
        fontWeight="300"
        order={{ base: 1, lg: 1 }} // First order on small screens
        textAlign={{ base: 'center', lg: 'left' }}
      >
        {t('showX-YfromZ', { startIndex, endIndex, total })}
      </Text>

      {/* Center - Pagination controls */}
      <Flex 
        alignItems="center" 
        gap={5}
        order={{ base: 2, lg: 2 }} // Second order on small screens
        justifyContent="center"
      >
        {/* Previous button */}
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          isDisabled={currentPage === 1 || isLoading}
          variant="outline"
          size="sm"
          mr={0}
          fontWeight="400"
          borderRadius="10px"
          color="alea.500"
          height="30px"
          width={{ base: "30px", lg: "auto" }}
          minW={{ base: "30px", lg: "auto" }}
          px={{ base: 0, lg: "10px" }}
        >
          <AleaArrowLeftIcon size={12} />
          <Text display={{ base: "none", lg: "block" }} ml={2}>
            {t('previous')}
          </Text>
        </Button>

        {/* Page numbers */}
        <Flex gap={1}>
          {getPageNumbers().map((page, index) => {
            if (page === '...') {
              return (
                <Text
                  key={`ellipsis-${index}`}
                  px={2}
                  py={1}
                  fontSize="sm"
                  color="gray.500"
                  minW="30px"
                  textAlign="center"
                >
                  ...
                </Text>
              );
            }
            
            return (
              <Button
                key={page}
                onClick={() => handlePageChange(page as number)}
                bg={page === currentPage ? "alea.500" : "transparent"}
                color={page === currentPage ? "white" : "gray.600"}
                _hover={{
                  bg: page === currentPage ? "alea.600" : "gray.100",
                }}
                fontSize="16px"
                fontWeight="400"
                w="30px"
                h="30px"
                minW="30px"
                borderRadius="10px"
                px={0}
              >
                {page}
              </Button>
            );
          })}
        </Flex>

        {/* Next button */}
        <Button
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={currentPage === totalPages || isLoading}
          variant="outline"
          size="sm"
          fontWeight="400"
          borderRadius="10px"
          color="alea.500"
          height="30px"
          width={{ base: "30px", lg: "auto" }}
          minW={{ base: "30px", lg: "auto" }}
          px={{ base: 0, lg: "10px" }}
        >
          <Text display={{ base: "none", lg: "block" }} mr={2}>
            {t('next')}
          </Text>
          <AleaArrowRightIcon size={12} />
        </Button>
      </Flex>

      {/* Right side - Per page selector */}
      <Flex 
        alignItems="center" 
        gap={2}
        order={{ base: 3, lg: 3 }} // Third order on small screens
        justifyContent={{ base: 'center', lg: 'flex-end' }}
      >
        <Text fontSize="sm" color="alea.500" fontWeight={300}>
          {t('entriesPerPage')}
        </Text>
        <Select
          size="sm"
          value={effectivePerPage.toString()}
          onChange={handlePerPageChange}
          width="80px"
          fontSize="sm"
          isDisabled={isLoading}
        >
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </Select>
      </Flex>
    </Flex>
  );
};

export default AleaPagination;